import { useMemo } from 'react';
import { useApolloClient, useMutation } from '@apollo/client/react';
import {
  STAFF_PROXY_ADD_COMMAND_EXECUTION,
  STAFF_PROXY_MDM_COMMAND,
  STAFF_PROXY_TESTING_DEVICE_MDM_DETAILS,
  STAFF_PROXY_TESTING_EXECUTE_REMOTE_COMMAND,
  STAFF_PROXY_TESTING_UPDATE_DEVICE,
  STAFF_PROXY_TOGGLE_DEVICE_FEATURE,
} from '@staff-app/graphql-client';
import { safeJsonParse } from '@/lib/safe-format';
import { customerReadVars } from '../lib/drawer-mode';

export interface TestingDeviceCommands {
  /** staffProxyTestingUpdateDevice — `payload` must already be in the allowlisted shape. */
  updateDevice: (payload: Record<string, unknown>) => Promise<void>;
  /** staffProxyTestingExecuteRemoteCommand — one device, free-form command. */
  executeRemoteCommand: (command: string) => Promise<boolean>;
  /** staffProxyAddCommandExecution — resolves to open-api's parsed reply (needs-response data). */
  addCommandExecution: (command: string, value?: string) => Promise<unknown>;
  /** staffProxyMdmCommand for this one device. */
  mdmCommand: (command: string, value?: string) => Promise<void>;
  /** staffProxyToggleDeviceFeature for this one device (LOCKDOWN / PLAYBACK_CONTROL / REMOTE_CONTROL). */
  toggleFeature: (featureKey: string, featureValue: boolean) => Promise<void>;
  /** staffProxyTestingDeviceMdmDetails, always fresh (a read). Raw JSON string or null. */
  mdmDetails: (command: 'GET_TIMEZONE' | 'GET_WIFI') => Promise<string | null>;
}

/**
 * The drawer write ops the Settings tab and phase-3 dialogs share, bound to ONE device. Without
 * `accountId` (My Testing Devices) the accountId-taking ops refuse (403) a device outside the
 * OptiSigns Testing account server-side; with it (the customer drawer) updateDevice, execute and the
 * mdmDetails read go through staff-api's customer gate instead. The generic ops (addCommandExecution,
 * mdmCommand, toggleFeature) take no accountId and are sent exactly as before.
 */
export function useTestingDeviceCommands(
  deviceId: string,
  teamId: string | null | undefined,
  accountId?: string,
): TestingDeviceCommands {
  const client = useApolloClient();
  const [updateFn] = useMutation(STAFF_PROXY_TESTING_UPDATE_DEVICE);
  const [executeFn] = useMutation<{ staffProxyTestingExecuteRemoteCommand: boolean | null }>(
    STAFF_PROXY_TESTING_EXECUTE_REMOTE_COMMAND,
  );
  const [addExecFn] = useMutation<{ staffProxyAddCommandExecution: string | null }>(
    STAFF_PROXY_ADD_COMMAND_EXECUTION,
  );
  const [mdmFn] = useMutation(STAFF_PROXY_MDM_COMMAND);
  const [toggleFn] = useMutation(STAFF_PROXY_TOGGLE_DEVICE_FEATURE);

  return useMemo<TestingDeviceCommands>(
    () => ({
      updateDevice: async (payload) => {
        await updateFn({
          variables: { deviceId, payload: JSON.stringify(payload), ...customerReadVars(accountId) },
        });
      },
      executeRemoteCommand: async (command) => {
        const { data } = await executeFn({
          variables: { deviceId, command, ...customerReadVars(accountId) },
        });
        return Boolean(data?.staffProxyTestingExecuteRemoteCommand);
      },
      addCommandExecution: async (command, value) => {
        const { data } = await addExecFn({
          variables: {
            payload: {
              teamId: teamId ?? '',
              payloadJson: JSON.stringify({ deviceId, command, value: value ?? '' }),
              isNeedResponse: false,
            },
          },
        });
        return safeJsonParse<unknown>(data?.staffProxyAddCommandExecution, null);
      },
      mdmCommand: async (command, value) => {
        await mdmFn({ variables: { payload: { deviceIds: [deviceId], command, value } } });
      },
      toggleFeature: async (featureKey, featureValue) => {
        await toggleFn({
          variables: {
            payload: {
              deviceIds: [deviceId],
              payloadJson: JSON.stringify({
                deviceIds: [deviceId],
                featureKey: featureKey.toUpperCase(),
                featureValue,
              }),
            },
          },
        });
      },
      mdmDetails: async (command) => {
        const { data } = await client.query<{ staffProxyTestingDeviceMdmDetails: string | null }>({
          query: STAFF_PROXY_TESTING_DEVICE_MDM_DETAILS,
          variables: { deviceId, command, fresh: true, ...customerReadVars(accountId) },
          fetchPolicy: 'network-only',
        });
        return data?.staffProxyTestingDeviceMdmDetails ?? null;
      },
    }),
    [deviceId, teamId, accountId, client, updateFn, executeFn, addExecFn, mdmFn, toggleFn],
  );
}
